import { useState, useEffect, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTranslate } from 'react-admin';

interface LoginFailModalProps {
  open: boolean;
  onClose: () => void;
  retryAfter: number;
}

const formatCountdown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const LoginFailModal = ({ open, onClose, retryAfter }: LoginFailModalProps) => {
  const translate = useTranslate();
  const [countdown, setCountdown] = useState(retryAfter);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (open) {
      setCountdown(retryAfter);
    }
  }, [open, retryAfter]);

  useEffect(() => {
    if (!open) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onCloseRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="login-fail-dialog-title"
    >
      <DialogTitle
        id="login-fail-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <AccessTimeIcon />
        {translate('notification.login_fail_modal.title')}
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', mt: 2 }}>
        <Typography paragraph>
          {translate('notification.login_fail_modal.message')}
        </Typography>
        <Typography
          variant="h2"
          color="primary"
          sx={{ fontWeight: 'bold', my: 2 }}
        >
          {formatCountdown(countdown)}
        </Typography>
        <Typography color="text.secondary">
          {translate('notification.login_fail_modal.wait_message')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button onClick={onClose}>
          {translate('notification.login_fail_modal.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginFailModal;

